/**
 * ToolRegistry — DRAW-mode tool resolution + the pure create factory.
 *
 * Mirrors `RendererRegistry`: a `Map<ToolId, ShapeToolDefinition>` seeded
 * with built-ins on first resolve; custom registrations are never
 * clobbered. `createObjectFromTool` is the ONLY object constructor the
 * draw interaction uses — pure and unit-tested.
 *
 * @module floorplan/tools/ToolRegistry
 * @category FloorPlan
 */

import type { Bounds } from '../models/geometry';
import type { FloorObject } from '../models/object';
import { FLOOR_OBJECT_TYPE } from '../models/object';
import type { FloorEngineState } from '../stores/useFloorEngineStore/initializer';
import { createFloorId } from '../utils/id';
import type { ShapeToolDefinition } from './types';

const registry = new Map<string, ShapeToolDefinition>();

// Const-object holder (not a module-level `let`) per the app-wide lint.
const registryState = { builtInsRegistered: false };

/** Register (or override) a tool. */
export const registerTool = (tool: ShapeToolDefinition): void => {
  registry.set(tool.id, tool);
};

export const unregisterTool = (id: string): void => {
  registry.delete(id);
};

/** Resolve a tool id; null when unknown (caller ignores the gesture). */
export const resolveTool = (id: string): ShapeToolDefinition | null => {
  if (!registryState.builtInsRegistered) registerBuiltInTools();
  const tool = registry.get(id);
  return tool === undefined ? null : tool;
};

/** All registered tools (for toolbar UIs), built-ins included. */
export const listTools = (): ShapeToolDefinition[] => {
  if (!registryState.builtInsRegistered) registerBuiltInTools();
  return [...registry.values()];
};

/**
 * The layer new objects land on: the first VISIBLE, UNLOCKED layer in
 * render order (falls back to the first layer — a plan always has ≥1).
 */
export const resolveTargetLayerId = (
  state: Pick<FloorEngineState, 'layers'>,
): string | null => {
  for (const layer of state.layers) {
    if (layer.visible && !layer.locked) return layer.id;
  }
  const first = state.layers[0];
  return first === undefined ? null : first.id;
};

/** Next zIndex on a layer (top of stack). */
export const nextZIndexForLayer = (
  state: Pick<FloorEngineState, 'objectsById' | 'layerObjectIds'>,
  layerId: string,
): number => {
  const ids = state.layerObjectIds[layerId];
  if (ids === undefined || ids.length === 0) return 1;
  let max = 0;
  for (const id of ids) {
    const object = state.objectsById[id];
    if (object !== undefined && object.zIndex > max) max = object.zIndex;
  }
  return max + 1;
};

export type CreateObjectOptions = {
  layerId: string;
  zIndex: number;
  /** Overrides the tool's default label (localized by the chrome layer). */
  text?: string;
};

/** Build a new object from a tool over a world-space bounds. Pure. */
export const createObjectFromTool = (
  tool: ShapeToolDefinition,
  bounds: Bounds,
  options: CreateObjectOptions,
): FloorObject => {
  const style =
    tool.defaultStyle === undefined && options.text === undefined
      ? undefined
      : {
          ...tool.defaultStyle,
          ...(options.text === undefined ? {} : { text: options.text }),
        };
  const object: FloorObject = {
    id: createFloorId('obj'),
    type: tool.objectType,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    rotation: 0,
    scale: 1,
    visible: true,
    locked: false,
    zIndex: options.zIndex,
    layerId: options.layerId,
    metadata:
      tool.defaultMetadata === undefined ? {} : { ...tool.defaultMetadata },
  };
  if (style !== undefined) object.style = style;
  // Line objects carry their geometry as points (the drag vector).
  if (tool.objectType === FLOOR_OBJECT_TYPE.LINE) {
    object.points = [
      { x: 0, y: 0 },
      { x: bounds.width, y: bounds.height },
    ];
  }
  return object;
};

/** Built-in tools — presets over the built-in renderers. Idempotent. */
export const registerBuiltInTools = (): void => {
  registryState.builtInsRegistered = true;
  const setIfVacant = (tool: ShapeToolDefinition): void => {
    if (!registry.has(tool.id)) registry.set(tool.id, tool);
  };

  setIfVacant({
    id: 'rectangle',
    objectType: FLOOR_OBJECT_TYPE.RECT,
    defaultSize: { width: 80, height: 60 },
    dragToSize: true,
  });
  setIfVacant({
    id: 'circle',
    objectType: FLOOR_OBJECT_TYPE.CIRCLE,
    defaultSize: { width: 60, height: 60 },
    dragToSize: true,
  });
  setIfVacant({
    id: 'line',
    objectType: FLOOR_OBJECT_TYPE.LINE,
    defaultSize: { width: 100, height: 0 },
    dragToSize: true,
    minSize: 2,
  });
  setIfVacant({
    id: 'text',
    objectType: FLOOR_OBJECT_TYPE.TEXT,
    defaultSize: { width: 120, height: 30 },
    defaultStyle: { text: 'Text' },
    dragToSize: false,
  });
  setIfVacant({
    id: 'wall',
    objectType: FLOOR_OBJECT_TYPE.WALL,
    defaultSize: { width: 200, height: 12 },
    dragToSize: true,
  });
  setIfVacant({
    id: 'room',
    objectType: FLOOR_OBJECT_TYPE.ROOM,
    defaultSize: { width: 300, height: 200 },
    dragToSize: true,
    minSize: 40,
  });
  setIfVacant({
    id: 'table',
    objectType: FLOOR_OBJECT_TYPE.TABLE,
    defaultSize: { width: 88, height: 64 },
    dragToSize: true,
  });
  setIfVacant({
    id: 'chair',
    objectType: FLOOR_OBJECT_TYPE.CHAIR,
    defaultSize: { width: 34, height: 34 },
    dragToSize: false,
  });
  setIfVacant({
    id: 'counter',
    objectType: FLOOR_OBJECT_TYPE.COUNTER,
    defaultSize: { width: 200, height: 50 },
    dragToSize: true,
  });
  setIfVacant({
    id: 'door',
    objectType: FLOOR_OBJECT_TYPE.DOOR,
    defaultSize: { width: 60, height: 10 },
    dragToSize: true,
  });
  setIfVacant({
    id: 'window',
    objectType: FLOOR_OBJECT_TYPE.WINDOW,
    defaultSize: { width: 100, height: 8 },
    dragToSize: true,
  });
};
